import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/toaster';
import { ThemeProvider } from '@/components/theme-provider';
import { AuthProvider } from '@/contexts/auth-context';
import { ResumeProvider } from '@/contexts/resume-context';
import { inject } from "@vercel/analytics";
import Layout from '@/components/layout';
import HomePage from '@/pages/home';
import LoginPage from '@/pages/login';
import RegisterPage from '@/pages/register';
import WaitlistPage from '@/pages/waitlist';
import ProfilePage from '@/pages/profile';
import EvaluatePage from '@/pages/evaluate';
import DashboardPage from '@/pages/dashboard';
import PricingPage from '@/pages/pricing';
import ProjectsPage from '@/pages/projects';
import SearchPage from '@/pages/search';
import PlannerPage from '@/pages/planner';
import PersonalityResultsPage from '@/pages/planner/results';
import ProtectedRoute from '@/components/protected-route';
import NotificationsPage from '@/pages/notifications';
import ApplicationsPage from '@/pages/applications';
import LoopsPage from '@/pages/loops';
import CompanyPage from '@/pages/company';
import OnboardingPage from '@/pages/onboarding/index';
import ProfessionalsPage from '@/pages/marketing/professionals';
import EmployersPage from '@/pages/marketing/employers';
import AboutPage from '@/pages/about';
import VisionPage from '@/pages/about/vision';
import CompanyAboutPage from '@/pages/about/company';
import ValuesPage from '@/pages/about/values';
import BlogPage from '@/pages/about/blog';
import PrivacyPage from '@/pages/privacy';
import TermsPage from '@/pages/terms';
import CookiesPage from '@/pages/cookies';
import SettingsPage from '@/pages/settings';
import AdminDashboard from '@/pages/admin';
import AdminTalentPage from '@/pages/admin/talent';
import AdminTalentProfilePage from '@/pages/admin/talent-profile';
import AdminProjectsPage from '@/pages/admin/projects';
import ProjectDetailsPage from '@/pages/admin/project-details';
import AdminCompaniesPage from '@/pages/admin/companies';
import AdminSupportPage from '@/pages/admin/support';
import AdminUtilitiesPage from '@/pages/admin/utilities';
import InterviewDetailsPage from '@/pages/interviews/[id]';
import PostRegistrationHandler from '@/components/post-registration-handler';

// Initialize Vercel Analytics
inject();

function App() {
  return (
    <Router>
      <ThemeProvider defaultTheme="system" storageKey="resume-theme">
        <AuthProvider>
          <ResumeProvider>
            <PostRegistrationHandler />
            
            <Routes>
              <Route path="/" element={<Layout />}>
                <Route index element={<HomePage />} />
                <Route path="login" element={<LoginPage />} />
                <Route path="register" element={<RegisterPage />} />
                <Route path="waitlist" element={<WaitlistPage />} />
                <Route path="pricing" element={<PricingPage />} />
                <Route path="marketing">
                  <Route path="professionals" element={<ProfessionalsPage />} />
                  <Route path="employers" element={<EmployersPage />} />
                </Route>
                <Route path="about" element={<AboutPage />} />
                <Route path="about/vision" element={<VisionPage />} />
                <Route path="about/company" element={<CompanyAboutPage />} />
                <Route path="about/values" element={<ValuesPage />} />
                <Route path="about/blog" element={<BlogPage />} />
                <Route path="privacy" element={<PrivacyPage />} />
                <Route path="terms" element={<TermsPage />} />
                <Route path="cookies" element={<CookiesPage />} />
                <Route path="p/:customUrl" element={<ProfilePage />} />
                <Route path="profile/:id" element={<ProfilePage />} />
                <Route 
                  path="evaluate/:id" 
                  element={
                    <ProtectedRoute requiresHireLicense>
                      <EvaluatePage />
                    </ProtectedRoute>
                  } 
                />

                {/* Admin Routes */}
                <Route path="admin">
                  <Route 
                    index
                    element={
                      <ProtectedRoute requiresAdminMode>
                        <AdminDashboard />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="talent" 
                    element={
                      <ProtectedRoute requiresAdminMode>
                        <AdminTalentPage />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="talent/:id" 
                    element={
                      <ProtectedRoute requiresAdminMode>
                        <AdminTalentProfilePage />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="projects" 
                    element={
                      <ProtectedRoute requiresAdminMode>
                        <AdminProjectsPage />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="projects/:id" 
                    element={
                      <ProtectedRoute requiresAdminMode>
                        <ProjectDetailsPage />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="companies" 
                    element={
                      <ProtectedRoute requiresAdminMode>
                        <AdminCompaniesPage />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="support" 
                    element={
                      <ProtectedRoute requiresAdminMode>
                        <AdminSupportPage />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="utilities" 
                    element={
                      <ProtectedRoute requiresAdminMode>
                        <AdminUtilitiesPage />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="company/:id" 
                    element={
                      <ProtectedRoute requiresAdminMode>
                        <CompanyPage />
                      </ProtectedRoute>
                    } 
                  />
                </Route>

                {/* Protected Routes */}
                <Route path="company/:id" element={
                  <ProtectedRoute>
                    <CompanyPage />
                  </ProtectedRoute>
                } />
                <Route path="search" element={<SearchPage />} />
                <Route path="planner" element={<ProtectedRoute><PlannerPage /></ProtectedRoute>} />
                <Route path="planner/results" element={<ProtectedRoute><PersonalityResultsPage /></ProtectedRoute>} />
                <Route path="interviews/:id" element={<InterviewDetailsPage />} />
                <Route path="settings/*" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
                <Route path="onboarding" element={<ProtectedRoute><OnboardingPage /></ProtectedRoute>} />
                <Route path="dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
                <Route path="dashboard/applications" element={
                  <ProtectedRoute requiresHireLicense>
                    <ApplicationsPage />
                  </ProtectedRoute>
                } />
                <Route path="dashboard/applications/:id" element={
                  <ProtectedRoute requiresHireLicense>
                    <ApplicationsPage />
                  </ProtectedRoute>
                } />
                <Route path="projects" element={
                  <ProtectedRoute requiresHireLicense>
                    <ProjectsPage />
                  </ProtectedRoute>
                } />
                <Route path="projects/:id" element={
                  <ProtectedRoute requiresHireLicense>
                    <ProjectsPage />
                  </ProtectedRoute>
                } />
                <Route path="loops" element={<ProtectedRoute><LoopsPage /></ProtectedRoute>} />
                <Route path="notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />

                {/* Catch all redirect */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Route>
            </Routes>
            <Toaster />
          </ResumeProvider>
        </AuthProvider>
      </ThemeProvider>
    </Router>
  );
}

export default App;